import { OrgSessionEvents } from "@platform/constants/Events";
import { apiBaseUrls } from "@platform/constants/shared-constants";
import { eventBus } from "@platform/events/EventBus";
import API from "@platform/http/shared-http";
import debugLogger from "@platform/utils/debugLogger";

export interface SelectOrgResult {
  orgId: number;
  orgAlias: string;
}

/**
 * OrgSessionManager — single source of truth for the current org-scoped session.
 *
 * The session JWT lives in an httpOnly cookie managed by the API. The UI never
 * touches the token directly. This manager tracks the in-memory org context
 * (orgAlias, orgId) derived from API responses so consumers can read the
 * current org without a round-trip.
 *
 * Lifecycle:
 *   1. `initialize()` — check session validity via GET /auth/me on app startup
 *   2. `selectOrg()` — atomic org switch: API call → update in-memory state → emit event
 *   3. `clearSession()` — logout path: clear in-memory state
 */
class OrgSessionManager {
  private currentOrgAlias: string | null = null;
  private currentOrgId: number | null = null;
  private switching = false;
  private switchPromise: Promise<SelectOrgResult> | null = null;

  /**
   * Initialize from a session check. Called once during app startup.
   * The API validates the httpOnly cookie and returns the current user + org context.
   */
  initialize(orgAlias: string | null, orgId: number | null): void {
    if (orgAlias) {
      this.currentOrgAlias = orgAlias;
      this.currentOrgId = orgId;
      debugLogger.info("org-session", "Initialized with org context", orgAlias);
    } else {
      debugLogger.info("org-session", "Initialized without org context");
    }
  }

  /**
   * Get the current org alias from in-memory state.
   */
  getOrgAlias(): string | null {
    return this.currentOrgAlias;
  }

  /**
   * Get the current org ID from in-memory state.
   */
  getOrgId(): number | null {
    return this.currentOrgId;
  }

  /**
   * Whether an org switch is in progress.
   * API clients should wait for the switch to complete before making requests.
   */
  isSwitching(): boolean {
    return this.switching;
  }

  /**
   * Wait for any in-progress org switch to complete.
   * Returns immediately if no switch is in progress.
   */
  async waitForSwitch(): Promise<void> {
    if (this.switchPromise) {
      await this.switchPromise;
    }
  }

  /**
   * Atomically switch to a different organization.
   *
   * 1. Set switching flag (blocks new API requests via interceptor)
   * 2. Call POST /auth/select-org — API sets a new httpOnly cookie with the org-scoped JWT
   * 3. On success: update in-memory org state, emit ORG_SESSION_CHANGED
   * 4. On failure: emit ORG_SWITCH_FAILED, leave previous session intact
   */
  async selectOrg(orgAlias: string): Promise<SelectOrgResult> {
    if (this.switching) {
      throw new Error("Org switch already in progress");
    }

    this.switching = true;
    this.switchPromise = this.doSelectOrg(orgAlias);

    try {
      const result = await this.switchPromise;
      return result;
    } finally {
      this.switching = false;
      this.switchPromise = null;
    }
  }

  private async doSelectOrg(orgAlias: string): Promise<SelectOrgResult> {
    try {
      const baseUrl = apiBaseUrls.BACKEND;
      const api = new API(baseUrl, "/api/v1/auth/select-org");
      const response = await api.post<SelectOrgResult>({ orgAlias });

      // Update in-memory org state (token is in the httpOnly cookie, not here)
      this.currentOrgAlias = response.orgAlias;
      this.currentOrgId = response.orgId;

      debugLogger.info("org-session", "Org switched", response.orgAlias);

      eventBus.emitGlobal(OrgSessionEvents.ORG_SESSION_CHANGED, {
        orgAlias: response.orgAlias,
        orgId: response.orgId,
      });

      return response;
    } catch (error) {
      debugLogger.error("org-session", "Org switch failed", error);

      eventBus.emitGlobal(OrgSessionEvents.ORG_SWITCH_FAILED, {
        orgAlias,
        error: error instanceof Error ? error.message : "Unknown error",
      });

      throw error;
    }
  }

  /**
   * Clear the entire session. Called on logout.
   * In-memory state is cleared; the httpOnly cookie is cleared by the API logout endpoint.
   */
  clearSession(): void {
    this.currentOrgAlias = null;
    this.currentOrgId = null;
    this.switching = false;
    this.switchPromise = null;
  }

  /**
   * Update in-memory org context when the session is restored from a cookie.
   * Called after a successful GET /auth/me response.
   */
  onSessionRestored(orgAlias: string | null, orgId: number | null): void {
    this.currentOrgAlias = orgAlias;
    this.currentOrgId = orgId;
  }
}

export const orgSessionManager = new OrgSessionManager();
